/**
 * FAQPage.js - Frequently Asked Questions page for LeagueMaker
 * Accessible at /faq (public) and linked from HelpPage and LandingPage footer
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import Logo from './Logo';
import './FAQPage.css';

const FAQ_SECTIONS = [
  {
    category: 'General',
    items: [
      {
        question: 'What is LeagueMaker?',
        answer: (
          <p>LeagueMaker is a web app for organizing rec league sports schedules. You enter your teams, game dates, and time slots, and our algorithm generates a balanced schedule where every team gets fair game times. It also handles score tracking, standings, playoffs, captain portals, email notifications, and more.</p>
        ),
      },
      {
        question: 'Who is LeagueMaker for?',
        answer: (
          <p>Anyone who runs a recreational sports league — whether it's adult kickball, youth soccer, bar trivia, pool, darts, or anything in between. If you're scheduling games and tracking scores for a group of teams, LeagueMaker is built for you.</p>
        ),
      },
      {
        question: 'How is LeagueMaker different from TeamSnap or LeagueApps?',
        answer: (
          <>
            <p>LeagueMaker is purpose-built for <strong>scheduling fairness</strong>. Our algorithm guarantees that every team gets an equal number of early and late game times — something most platforms don't even attempt.</p>
            <p>We're also simpler and more affordable. No bloated feature sets, no per-player fees. You get clean scheduling, score tracking, captain portals, and public pages without the enterprise overhead.</p>
          </>
        ),
      },
    ],
  },
  {
    category: 'Pricing',
    items: [
      {
        question: 'Is there a free tier?',
        answer: (
          <p>Yes! The Pickup (free) plan lets you create 1 league with up to 8 teams, generate schedules, export draft PDFs, and share a public league page. No credit card required. <Link to="/pricing">See all plans</Link>.</p>
        ),
      },
      {
        question: 'What do I get with each plan?',
        answer: (
          <>
            <p><strong>Pickup (Free)</strong> — 1 league, up to 8 teams, draft PDF export, public page.</p>
            <p><strong>League (Starter)</strong> — Unlimited teams, clean PDFs, email notifications, captain portal, venue management.</p>
            <p><strong>Commissioner (Pro)</strong> — Unlimited leagues, score entry, captain and referee score submissions, playoffs, season archiving, referee management, and priority support.</p>
            <p><Link to="/pricing">Compare plans in detail</Link>.</p>
          </>
        ),
      },
      {
        question: 'Can I upgrade or downgrade my plan?',
        answer: (
          <p>Plan management is coming soon. During the beta period, all features are available for free. When paid plans launch, you'll be able to upgrade or downgrade from the Settings page at any time.</p>
        ),
      },
    ],
  },
  {
    category: 'Scheduling',
    items: [
      {
        question: 'How does the fairness algorithm work?',
        answer: (
          <>
            <p>The algorithm optimizes for three priorities:</p>
            <ol>
              <li><strong>Early/late balance</strong> — Every team gets an equal number of games at the earliest and latest time slots.</li>
              <li><strong>Overall distribution</strong> — All time slots are spread evenly across teams.</li>
              <li><strong>Matchup balance</strong> — Round-robin cycling ensures every team plays every other team a similar number of times.</li>
            </ol>
            <p>It runs multiple attempts with different random seeds and keeps the fairest result. Most schedules earn an "A" fairness grade. <Link to="/help">Learn more in the Help guide</Link>.</p>
          </>
        ),
      },
      {
        question: 'Can I edit the schedule after generating it?',
        answer: (
          <p>Yes. While your league is in Draft mode, you can edit individual game dates, times, and team matchups from the Game Management page. You can also swap two games' time slots. The fairness report updates automatically after each edit so you can see if it affected balance.</p>
        ),
      },
      {
        question: 'What sports are supported?',
        answer: (
          <>
            <p>LeagueMaker supports 10 sport types: <strong>General, Soccer, Basketball, Volleyball, Softball, Hockey, Football, Win/Loss Only, Pool, and Darts</strong>.</p>
            <p>Each sport has its own scoring rules, standings tiebreakers, and stat columns. Pool and Darts also support best-of series formats. The "Win/Loss Only" mode works great for any league that just needs to track wins and losses without numeric scores.</p>
          </>
        ),
      },
    ],
  },
  {
    category: 'Features',
    items: [
      {
        question: 'Can team captains submit scores?',
        answer: (
          <p>Yes, on the Commissioner (Pro) plan. Enable "Captain Score Submission" on your league, then generate captain links. Each captain gets a magic link to their portal where they can submit scores. Submissions go to your review queue — you approve or reject each one before it affects standings. <Link to="/help">See the full walkthrough</Link>.</p>
        ),
      },
      {
        question: 'How do calendar subscriptions work?',
        answer: (
          <p>Every public league page has a Calendar tab where people can subscribe to an iCal feed — either for the full league or for a specific team. It works with Apple Calendar, Google Calendar, Outlook, and most other apps. The feed updates automatically as you enter scores or reschedule games.</p>
        ),
      },
      {
        question: 'Can I export my schedule?',
        answer: (
          <>
            <p>Yes, in two ways:</p>
            <ul>
              <li><strong>PDF export</strong> — Download a professional PDF with a title page, full league schedule, and individual team pages. Available on all plans (free plan PDFs include a DRAFT watermark).</li>
              <li><strong>JSON export</strong> — Admins can export full league data as JSON for backup or re-import.</li>
            </ul>
          </>
        ),
      },
      {
        question: 'Can I embed the schedule on my own website?',
        answer: (
          <p>Yes! Once your league is published and public, click the "Embed" button on the Dashboard. You'll get an iframe code snippet you can paste into any website — Wix, Squarespace, WordPress, or plain HTML. The widget auto-resizes and updates as you enter scores.</p>
        ),
      },
    ],
  },
  {
    category: 'Account',
    items: [
      {
        question: 'How do I reset my password?',
        answer: (
          <p>On the login page, click "Forgot Password?" and enter your email. You'll receive a reset link that's valid for 1 hour. If you're already logged in, you can change your password from the Settings page.</p>
        ),
      },
      {
        question: 'Can I have multiple leagues?',
        answer: (
          <p>On the Pickup (Free) and League (Starter) plans, you can have 1 active league at a time. Completed leagues don't count toward this limit, so you can mark a league done and start a new one. On the Commissioner (Pro) plan, you can have unlimited active leagues.</p>
        ),
      },
      {
        question: 'How do I delete my account?',
        answer: (
          <p>Account deletion is handled by our support team. Email <a href="mailto:support@leaguemaker.app">support@leaguemaker.app</a> and we'll take care of it. All your leagues, teams, and data will be permanently removed.</p>
        ),
      },
    ],
  },
];

function FAQAccordion({ question, answer, isOpen, onToggle }) {
  return (
    <div className={`faq-accordion ${isOpen ? 'faq-accordion-open' : ''}`}>
      <button className="faq-accordion-header" onClick={onToggle} aria-expanded={isOpen}>
        <span className="faq-accordion-question">{question}</span>
        <span className="faq-accordion-icon">{isOpen ? '\u2212' : '+'}</span>
      </button>
      {isOpen && (
        <div className="faq-accordion-body">
          {answer}
        </div>
      )}
    </div>
  );
}

export default function FAQPage() {
  const { theme, toggleTheme } = useTheme();
  // Track open state as "sectionIndex-itemIndex" string
  const [openKey, setOpenKey] = useState(null);

  // Override body background for public route
  useEffect(() => {
    const root = document.documentElement;
    const body = document.body;
    const origRootBg = root.style.background;
    const origBodyBg = body.style.background;
    root.style.background = 'var(--color-bg)';
    body.style.background = 'var(--color-bg)';
    return () => {
      root.style.background = origRootBg;
      body.style.background = origBodyBg;
    };
  }, []);

  const toggleItem = (key) => {
    setOpenKey(openKey === key ? null : key);
  };

  return (
    <div className="faq-page">
      <nav className="faq-nav">
        <Link to="/" className="faq-nav-logo">
          <Logo size="1.4rem" /> LeagueMaker
        </Link>
        <div className="faq-nav-actions">
          <button
            className="faq-theme-toggle"
            onClick={toggleTheme}
            title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
          >
            {theme === 'dark' ? '\u2600' : '\uD83C\uDF19'}
          </button>
          <Link to="/" className="faq-nav-link">Back to Home</Link>
        </div>
      </nav>

      <div className="faq-content">
        <div className="faq-hero">
          <h1 className="faq-hero-title">Frequently Asked Questions</h1>
          <p className="faq-hero-subtitle">
            Quick answers to common questions about LeagueMaker.
          </p>
        </div>

        {FAQ_SECTIONS.map((section, sIndex) => (
          <div key={sIndex} className="faq-category">
            <h2 className="faq-category-title">{section.category}</h2>
            <div className="faq-category-items">
              {section.items.map((item, iIndex) => {
                const key = `${sIndex}-${iIndex}`;
                return (
                  <FAQAccordion
                    key={key}
                    question={item.question}
                    answer={item.answer}
                    isOpen={openKey === key}
                    onToggle={() => toggleItem(key)}
                  />
                );
              })}
            </div>
          </div>
        ))}

        <div className="faq-bottom">
          <p className="faq-bottom-text">
            Can't find what you're looking for? Check the <Link to="/help" className="faq-inline-link">Help &amp; How-To guide</Link> for detailed walkthroughs, or reach out to us directly.
          </p>
          <a href="mailto:support@leaguemaker.app" className="faq-contact-link">
            support@leaguemaker.app
          </a>
        </div>
      </div>

      <footer className="faq-footer">
        <div className="faq-footer-links">
          <Link to="/terms" className="faq-footer-link">Terms</Link>
          <span className="faq-footer-separator">|</span>
          <Link to="/privacy" className="faq-footer-link">Privacy</Link>
        </div>
        <p>&copy; {new Date().getFullYear()} LeagueMaker. All rights reserved.</p>
      </footer>
    </div>
  );
}
